﻿import { toast } from 'sonner';
import { useAssessmentStore } from '../../store/assessment.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import i18next from "i18next";
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class AssessmentQuestionIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch assessment questions (documents) and update store
     */
    async fetchAssessmentQuestions(overrides = {}) {
        const store = useAssessmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        const page = overrides.page || store.questionPagination.currentPage || 1;
        const pageSize = overrides.pageSize || store.questionPagination.itemsPerPage || 10;
        const search = overrides.search !== undefined ? overrides.search : store.questionSearchQuery;
        const sortBy = overrides.sortBy !== undefined ? overrides.sortBy : (store.questionSortBy || '');
        const sortOrder = overrides.sortDirection !== undefined ? overrides.sortDirection : (store.questionSortDirection || 'asc');
        const columnFilters = overrides.columnFilters || store.questionColumnFilters || {};

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setAssessmentQuestions(data || []);

            if (pagination && store.setQuestionPagination) {
                store.setQuestionPagination({
                    currentPage: pagination.page,
                    itemsPerPage: pagination.pageSize,
                    totalItems: pagination.total,
                    totalPages: pagination.totalPages,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1
                });
            }

            if (uniqueValues && store.setQuestionUniqueValues) {
                store.setQuestionUniqueValues(uniqueValues);
            }

            return { success: true, data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async createQuestionDocument(data) {
        const store = useAssessmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.create({ ...data, tenantId });
            toast.success(i18next.t('internalAuditAssessmentQuestions:messages.createSuccess', { defaultValue: 'Evaluation question created successfully' }));
            this.fetchAssessmentQuestions();
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async updateQuestionDocument(id, data) {
        const store = useAssessmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('internalAuditAssessmentQuestions:messages.updateSuccess', { defaultValue: 'Evaluation question updated successfully' }));
            this.fetchAssessmentQuestions();
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteQuestionDocument(id) {
        const store = useAssessmentStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            await this.service.delete(id);
            toast.success(i18next.t('internalAuditAssessmentQuestions:messages.deleteSuccess', { defaultValue: 'Evaluation question deleted successfully' }));
            this.fetchAssessmentQuestions();
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async bulkDeleteQuestionDocuments(ids) {
        const store = useAssessmentStore.getState();
        store.setLoading(true);

        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t('internalAuditAssessmentQuestions:messages.bulkDeleteSuccess', { defaultValue: 'Selected questions deleted successfully' }));
            this.fetchAssessmentQuestions();
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async getById(id) {
        return this.service.getById(id);
    }

    async queryAll(params = {}) {
        return this.service.getAll(params);
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundDefault: 'Item not found',
        });
    }
}
